import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show, untrack } from "solid-js";
import { CheckCircle2, ExternalLink, Loader2, Plug, XCircle } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Label, TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";

type ErrorModel = components["schemas"]["ErrorModel"];
type ListResponse = components["schemas"]["IntegrationsListResponse"];
type IntegrationView = components["schemas"]["IntegrationView"];
type OrganizationDTO = components["schemas"]["OrganizationDTO"];
type UpdateBody = components["schemas"]["UpdateIntegrationBody"];

/** Forma mínima de un `config_schema` (JSON-Schema) que renderiza el formulario. */
interface JsonSchemaProp {
  type?: "string" | "integer" | "number" | "boolean" | "object" | "array";
  title?: string;
  description?: string;
  default?: unknown;
  example?: unknown;
  "x-secret"?: boolean;
}
interface JsonSchema {
  type?: string;
  properties?: Record<string, JsonSchemaProp>;
  required?: string[];
}

const GLOBAL_SCOPE = "__global__";

function isSecretField(spec: JsonSchemaProp): boolean {
  return spec["x-secret"] === true;
}

function isBoolField(spec: JsonSchemaProp): boolean {
  return spec.type === "boolean";
}

/** Coerción segura de un valor de config a string para un input (evita `[object Object]`). */
function toInputString(v: unknown): string {
  if (v === undefined || v === null) {
    return "";
  }
  if (typeof v === "string") {
    return v;
  }
  if (typeof v === "number" || typeof v === "boolean" || typeof v === "bigint") {
    return String(v);
  }
  return JSON.stringify(v);
}

export function IntegrationsPanel() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();
  // ReadOnly tiene `integrations.read` pero no `integrations.testEmail`; usamos
  // esa última como proxy de "puede escribir/probar" hasta que exista un cap propio.
  const canManage = () => can("integrations.testEmail");

  // Alcance: global o una organización concreta.
  const [scope, setScope] = createSignal<string>(GLOBAL_SCOPE);
  const orgId = createMemo(() => (scope() === GLOBAL_SCOPE ? undefined : scope()));

  const orgsQuery = createApiQuery<OrganizationDTO[], ErrorModel>(
    () => ["admin", "integrations", "orgs"] as const,
    async () => api.GET("/api/org/listOrganizations", {}),
    () => ({ staleTime: 60_000 }),
  );

  const query = createApiQuery<ListResponse, ErrorModel>(
    () => adminQueryKeys.integrations(orgId()),
    async () => api.GET("/api/admin/integrations", { params: { query: { org_id: orgId() } } }),
    () => ({ staleTime: 15_000 }),
  );

  const scopeOptions = createMemo(() => [
    { value: GLOBAL_SCOPE, label: t(msg`Global (platform default)`) },
    ...(orgsQuery.data ?? []).map((o) => ({ value: o.id, label: o.name })),
  ]);

  return (
    <div class="space-y-6">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3">
          <Plug class="h-8 w-8 text-primary" />
          <div>
            <h2 class="text-3xl font-bold tracking-tight">{t(msg`Integrations`)}</h2>
            <p class="text-sm text-muted-foreground">
              {t(
                msg`Connect email (SMTP), Stripe, the BCV rate and AI. Values resolve global → per-academy; secrets are sealed and never shown again. Use Test before saving.`,
              )}
            </p>
          </div>
        </div>
        <div class="w-full sm:w-64">
          <Label>{t(msg`Scope`)}</Label>
          <Select
            options={scopeOptions()}
            value={scope()}
            onChange={(v) => {
              setScope(v);
            }}
          />
        </div>
      </div>

      <Show
        when={!query.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 4 }, () => 0)}>
              {() => <Skeleton class="h-40 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="space-y-4">
          <For each={query.data?.data ?? []}>
            {(view) => (
              <IntegrationCard
                view={view}
                orgId={orgId()}
                canManage={canManage()}
                onChanged={() => {
                  void query.refetch();
                }}
              />
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

function IntegrationCard(props: {
  view: IntegrationView;
  orgId: string | undefined;
  canManage: boolean;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const schema = () => (props.view.configSchema ?? {}) as JsonSchema;
  const schemaProps = () => schema().properties ?? {};
  const requiredSet = () => new Set(schema().required);

  const initialConfig = () => {
    // `config` es `unknown` (JSON del backend): lo leemos como Map sin aserciones.
    const stored = new Map<string, unknown>();
    const raw = props.view.config;
    if (typeof raw === "object" && raw !== null) {
      for (const [k, v] of Object.entries(raw)) {
        stored.set(k, v);
      }
    }
    const out: Record<string, string> = {};
    for (const [key, spec] of Object.entries(schemaProps())) {
      if (isSecretField(spec)) {
        continue;
      }
      const current = stored.has(key) ? stored.get(key) : spec.default;
      out[key] = toInputString(current);
    }
    return out;
  };

  // Estado editable: valores no-secretos (string en el input) + secretos nuevos.
  const [config, setConfig] = createSignal<Record<string, string>>(untrack(initialConfig));
  const [secrets, setSecrets] = createSignal<Record<string, string>>({});
  const [enabled, setEnabled] = createSignal<boolean>(untrack(() => props.view.enabled));

  const saveMutation = createApiMutation(async (vars: { key: string; body: UpdateBody }) =>
    api.PUT("/api/admin/integrations/{key}", {
      params: { path: { key: vars.key }, query: { org_id: props.orgId } },
      body: vars.body,
    }),
  );
  const testMutation = createApiMutation(async (vars: { key: string }) =>
    api.POST("/api/admin/integrations/{key}/test", {
      params: { path: { key: vars.key }, query: { org_id: props.orgId } },
    }),
  );

  const secretIsSet = (field: string) => props.view.secretsSet.includes(field);
  const hasTestResult = () => props.view.configured && typeof props.view.lastTestOk === "boolean";

  const buildBody = (): UpdateBody => {
    const configOut: Record<string, unknown> = {};
    for (const [key, spec] of Object.entries(schemaProps())) {
      if (isSecretField(spec)) {
        continue;
      }
      const raw = config()[key];
      if (raw === undefined || raw === "") {
        continue;
      }
      if (spec.type === "integer") {
        const n = Number.parseInt(raw, 10);
        if (!Number.isNaN(n)) {
          configOut[key] = n;
        }
      } else if (spec.type === "number") {
        const n = Number.parseFloat(raw);
        if (!Number.isNaN(n)) {
          configOut[key] = n;
        }
      } else if (spec.type === "boolean") {
        configOut[key] = raw === "true";
      } else {
        configOut[key] = raw;
      }
    }
    // Solo enviamos secretos que el usuario tecleó (los vacíos conservan el valor).
    const secretsOut: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(secrets())) {
      if (value.trim() !== "") {
        secretsOut[key] = value;
      }
    }
    return { config: configOut, secrets: secretsOut, enabled: enabled() };
  };

  const handleSave = () => {
    saveMutation.mutate(
      { key: props.view.key, body: buildBody() },
      {
        onSuccess: () => {
          setSecrets({});
          showToast(t(msg`Saved`), "success");
          props.onChanged();
        },
        onError: (e: unknown) => {
          const m = e instanceof Error ? e.message : String(e);
          showToast(t(msg`Save failed: ${m}`), "error");
        },
      },
    );
  };

  const handleTest = () => {
    testMutation.mutate(
      { key: props.view.key },
      {
        onSuccess: (res) => {
          const r = res.result;
          const suffix = r.message ? `: ${r.message}` : "";
          showToast(
            r.ok ? t(msg`Test OK${suffix}`) : t(msg`Test failed${suffix}`),
            r.ok ? "success" : "error",
          );
          props.onChanged();
        },
        onError: (e: unknown) => {
          const m = e instanceof Error ? e.message : String(e);
          showToast(t(msg`Test error: ${m}`), "error");
        },
      },
    );
  };

  return (
    <Card>
      <CardContent class="p-5 space-y-4">
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-bold">{props.view.name}</span>
              <Badge variant="secondary">{props.view.status}</Badge>
              <Show when={hasTestResult()}>
                <Show
                  when={props.view.lastTestOk}
                  fallback={<XCircle class="h-4 w-4 text-destructive" />}
                >
                  <CheckCircle2 class="h-4 w-4 text-green-600" />
                </Show>
              </Show>
            </div>
            <p class="text-xs text-muted-foreground">{props.view.description}</p>
            <Show when={props.view.docsUrl}>
              {(url) => (
                <a
                  href={url()}
                  target="_blank"
                  rel="noreferrer"
                  class="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  {t(msg`Docs`)}
                  <ExternalLink class="h-3 w-3" />
                </a>
              )}
            </Show>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Label class="text-xs text-muted-foreground">{t(msg`Enabled`)}</Label>
            <Switch checked={enabled()} disabled={!props.canManage} onCheckedChange={setEnabled} />
          </div>
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <For each={Object.entries(schemaProps())}>
            {([field, spec]) => (
              <FieldInput
                field={field}
                spec={spec}
                required={requiredSet().has(field)}
                disabled={!props.canManage}
                secretSet={secretIsSet(field)}
                value={isSecretField(spec) ? (secrets()[field] ?? "") : (config()[field] ?? "")}
                onInput={(v) => {
                  if (isSecretField(spec)) {
                    setSecrets({ ...secrets(), [field]: v });
                  } else {
                    setConfig({ ...config(), [field]: v });
                  }
                }}
                onToggle={(v) => {
                  setConfig({ ...config(), [field]: String(v) });
                }}
              />
            )}
          </For>
        </div>

        <Show when={props.canManage}>
          <div class="flex justify-end gap-2 pt-1">
            <Button variant="ghost" disabled={testMutation.isPending} onClick={handleTest}>
              <Show when={testMutation.isPending}>
                <Loader2 class="mr-2 h-4 w-4 animate-spin" />
              </Show>
              {t(msg`Test connection`)}
            </Button>
            <Button disabled={saveMutation.isPending} onClick={handleSave}>
              {t(msg`Save`)}
            </Button>
          </div>
        </Show>
      </CardContent>
    </Card>
  );
}

function FieldInput(props: {
  field: string;
  spec: JsonSchemaProp;
  required: boolean;
  disabled: boolean;
  secretSet: boolean;
  value: string;
  onInput: (v: string) => void;
  onToggle: (v: boolean) => void;
}) {
  const { t } = useI18n();
  const label = () => props.spec.title ?? props.field;

  const inputType = (): "password" | "number" | "text" => {
    if (isSecretField(props.spec)) {
      return "password";
    }
    if (props.spec.type === "integer" || props.spec.type === "number") {
      return "number";
    }
    return "text";
  };

  const placeholder = (): string => {
    if (isSecretField(props.spec) && props.secretSet) {
      return t(msg`•••••••• (set — leave blank to keep)`);
    }
    return toInputString(props.spec.example);
  };

  return (
    <div class="space-y-1.5">
      <Label class="flex items-center gap-1">
        {label()}
        <Show when={props.required}>
          <span class="text-destructive">*</span>
        </Show>
      </Label>
      <Show
        when={!isBoolField(props.spec)}
        fallback={
          <div class="pt-1">
            <Switch
              checked={props.value === "true"}
              disabled={props.disabled}
              onCheckedChange={props.onToggle}
            />
          </div>
        }
      >
        <TextField
          type={inputType()}
          disabled={props.disabled}
          placeholder={placeholder()}
          value={props.value}
          onInput={(e) => {
            props.onInput(e.currentTarget.value);
          }}
        />
      </Show>
      <Show when={props.spec.description}>
        <p class="text-xs text-muted-foreground">{props.spec.description}</p>
      </Show>
    </div>
  );
}
